<template>
    <div class="grid"
         style="padding-left: 7px"
         :style="repeaterStyle">
        <ConfirmDialog header="Confirm delete"
                       question="Do you want to choose to delete selected row?"
                       v-model="isConfirmationOpen"
                       :operation="deleteRow"
                       :parameter="recordSelectedToDelete" />

        <Button v-if="isAddButton"
                style="margin-left: 3px"
                class="bg-emerald-300 hover:bg-green-200 text-black hover:text-green-600 font-medium py-1 px-2.5 rounded-md border border-gray-200 hover:border-gray-300 transition-colors duration-150 flex items-center gap-2"
                @click="addRow"
                :disabled="disabled"
                icon="pi pi-plus"
                :label="__(addButtonName)" />

        <Button v-if="isCopyButton && currentRecord?.length > 0"
                style="margin-left: 3px"
                class="bg-blue-300 hover:bg-blue-200 text-black hover:text-blue-600 font-medium py-1 px-2.5 rounded-md border border-gray-200 hover:border-gray-300 transition-colors duration-150 flex items-center gap-2"
                @click="copyLastRow"
                :disabled="disabled"
                icon="pi pi-copy"
                :label="__(copyLastButtonName)" />

        <table>
            <tbody>
                <tr>
                    <th v-if="isDeleteReplicate && isDeleteButton && !disabled"
                        :style="headerStyle"
                        :class="headerClass"><i class="fa-regular fa-trash-can"></i></th>

                    <th :style="headerStyle"
                        :class="headerClass"
                        v-for="(label, index) in title"
                        :key="index">{{ __(label) }}</th>

                    <th v-if="isDeleteButton && !disabled"
                        :style="headerStyle"
                        :class="headerClass"><i class="fa-regular fa-trash-can"></i></th>
                </tr>
                <tr v-for="(record, index) in currentRecord"
                    :key="record?.id ?? index"
                    :name="'repeater-' + index + '-' + name"
                    :forceLoop="forceLoop">

                    <td v-if="isDeleteReplicate && isDeleteButton && !disabled"
                        @click.stop="openConfirmationWindow(record)"
                        :style="cellStyle"
                        :class="cellClass"
                        style="font-weight: 900; color: red; cursor: pointer; font-size: 20px; vertical-align: top; padding-top: 10px">
                        <span>
                            <i v-tooltip.top="__('Delete') + (record?.id ? ' ' + record?.id : '')"
                               class="fa-solid fa-trash-can"></i>
                        </span>
                    </td>

                    <slot name="repeat"
                          :record="record"
                          :cellStyle="cellStyle"
                          :cellClass="cellClass"
                          :index="renameElements(index)" />

                    <td v-if="isDeleteButton && !disabled"
                        @click.stop="openConfirmationWindow(record)"
                        :style="cellStyle"
                        :class="cellClass"
                        style="font-weight: 900; color: red; cursor: pointer; font-size: 20px; vertical-align: top; padding-top: 10px">
                        <span>
                            <i v-tooltip.top="__('Delete') + (record?.id ? ' ' + record?.id : '')"
                               class="fa-solid fa-trash-can"></i>
                        </span>
                    </td>
                </tr>
                <!-- <tfoot> -->
                <tr>
                    <td v-if="isDeleteReplicate && isDeleteButton && !disabled"></td>
                    <slot name="footer"
                          :cellStyle="cellStyle"
                          :cellClass="cellClass"
                          :records="currentRecord"
                          :columns="title.length" />
                    <td v-if="isDeleteButton && !disabled"></td>
                </tr>
                <tr>
                    <td :colspan="title.length">
                        <slot name="mergedFooter"
                              :cellStyle="cellStyle"
                              :cellClass="cellClass"
                              :records="currentRecord" />
                    </td>
                </tr>
                <!-- </tfoot> -->
            </tbody>
        </table>
    </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import Button from 'primevue/button';
import ConfirmDialog from '@pages/Crud/ConfirmDialog.vue';
import { __ } from '@pages/Helper/utils.js';

const props = defineProps({
    keys: { type: Object, default: {} },
    name: { type: String, required: true },
    isFirstRowEmpty: { type: Boolean, default: false },
    isCopyButton: { type: Boolean, default: false },
    isAddButton: { type: Boolean, default: true },
    isDeleteButton: { type: Boolean, default: true },
    isDeleteReplicate: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    repeaterStyle: String,
    title: Array,
    customFunction: [Function, null],
    headerStyle: {
        type: [Object, String],
        default: () => ({
            border: '1px solid #dddddd',
            textAlign: 'left',
            padding: '8px',
            verticalAlign: 'top',
        }),
    },
    headerClass: {
        type: String,
        default: null,
    },
    cellStyle: {
        type: [Object, String],
        default: () => ({
            border: '1px solid #dddddd',
            textAlign: 'left',
            padding: '8px',
            verticalAlign: 'top',
        }),
    },
    cellClass: {
        type: String,
        default: null,
    },
    addButtonName: {
        type: String,
        default: 'Add',
    },
    copyLastButtonName: {
        type: String,
        default: 'Copy last',
    },
    addRowFunction: {
        type: Function,
        default: null,
    },
    copyLastRowFunction: {
        type: Function,
        default: null,
    },
    deleteRowFunction: {
        type: Function,
        default: null,
    },
});

const forceLoop = ref(false);
const isConfirmationOpen = ref(false);
const recordSelectedToDelete = ref(null);

const currentRecord = defineModel('record', { default: [] });

// ------------------------------------------------------------------------------

watch(
    () => currentRecord.value,
    (newValue, oldValue) => {
        if (currentRecord.value?.length == 0 && props.isFirstRowEmpty) {
            currentRecord.value = [{}];
        }

        forceLoop.value = !forceLoop.value;
    },
    { deep: true, immediate: true }
);

// ------------------------------------------------------------------------------

const deleteRow = (record) => {
    let newRecord = currentRecord.value.filter((elem) => {
        if (elem?.id && record?.id) {
            return elem.id !== record.id;
        } else {
            return elem?.__index !== record?.__index;
        }
    });

    currentRecord.value = [...newRecord];
    if (props.deleteRowFunction) {
        props.deleteRowFunction(record, currentRecord.value);
    }
    resetElements();
};

// ------------------------------------------------------------------------------

const addRow = () => {
    const keysClone = structuredClone(props.keys);  // deep clone — każdy wiersz dostaje własne kopie obiektów
    let newRecord = { id: null, __index: createRepeaterIndex(), files: [], ...keysClone };
    if (props.customFunction) {
        newRecord = props.customFunction(currentRecord.value);
    }

    currentRecord.value = [...(currentRecord?.value ?? []), newRecord];

    if (props.addRowFunction) {
        props.addRowFunction(newRecord, currentRecord.value);
    }
};

// ------------------------------------------------------------------------------

const copyLastRow = () => {
    if (currentRecord.value?.length > 0) {
        let newRecord = structuredClone(currentRecord.value[currentRecord.value.length - 1]);
        newRecord.id = null;
        newRecord.__index = createRepeaterIndex();
        currentRecord.value = [...(currentRecord?.value ?? []), newRecord];

        if (props.copyLastRowFunction) {
            props.copyLastRowFunction(newRecord, currentRecord.value);
        }
    } else {
        addRow();
    }
};

// ------------------------------------------------------------------------------

const waitForNextTick = async () => {
    await nextTick();
};

// ------------------------------------------------------------------------------

const renameElements = (index) => {
    waitForNextTick().then(() => {
        let repeaterTr = document?.querySelector("tr[name='repeater-" + index + '-' + props.name + "']");
        let elementsWithName = repeaterTr?.querySelectorAll('[name]');
        let currentName = null;

        elementsWithName?.forEach((element) => {
            currentName = element.getAttribute('name');
            if (!currentName.startsWith(props.name + '-')) {
                element.setAttribute('name', props.name + '-' + index + '-' + currentName);
                element.setAttribute('data-before', currentName);
            }
        });
    });

    return index;
};

// ------------------------------------------------------------------------------

const resetElements = () => {
    waitForNextTick().then(() => {
        let start = 'repeater-';
        let end = '-' + props.name;
        let repeaterTr = document?.querySelector(`tr[name^="${start}"][name$="${end}"]`);
        let elementsWithName = repeaterTr?.querySelectorAll('[name]');

        elementsWithName?.forEach((element) => {
            if (element.hasAttribute('data-before')) {
                element.setAttribute('name', element.getAttribute('data-before'));
                element.removeAttribute('data-before');
            }
        });
    });
};

// ------------------------------------------------------------------------------

const createRepeaterIndex = () => {
    return Date.now().toString() + Math.random().toString();
};

// ------------------------------------------------------------------------------

const openConfirmationWindow = (record) => {
    recordSelectedToDelete.value = record;
    isConfirmationOpen.value = true;
};

//-----------------------------------------------------------------------------------------------
</script>

<style lang="scss" scoped>
table {
    border-collapse: collapse;
    width: 100%;
    margin-top: 10px;
    margin-bottom: 20px;
}
</style>
